import { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IconButton } from '../design/primitives/Button';
import { HtmlDocument, needsDocumentRendering } from './HtmlDocument';

// Rich text on tiptap (StarterKit). Value is HTML. The read view sanitises: only a small
// allow-list of tags/attributes survives, so server-stored HTML can never run script.

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'CODE', 'PRE', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'A', 'HR']);

export function sanitizeHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]*>/g, '');
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        child.replaceWith(...Array.from(child.childNodes));
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        const ok = child.tagName === 'A' && attr.name === 'href' && /^(https?:|mailto:|tel:)/i.test(attr.value);
        if (!ok) child.removeAttribute(attr.name);
      }
      if (child.tagName === 'A') {
        child.setAttribute('rel', 'noopener noreferrer');
        child.setAttribute('target', '_blank');
      }
      walk(child);
    }
  };
  const root = doc.body.firstElementChild;
  if (!root) return '';
  walk(root);
  return root.innerHTML;
}

export function RichTextView({ html, clamp }: { html: string | null | undefined; clamp?: boolean }) {
  const safe = useMemo(() => sanitizeHtml(html ?? ''), [html]);
  if (!safe) return <span className="hf-muted">—</span>;
  // Authored documents (emails, Halo notes with tables/images/inline styles) render at full
  // fidelity in a sandboxed frame; a compact cell keeps the clamped inline text.
  if (!clamp && needsDocumentRendering(html ?? '')) return <HtmlDocument html={html ?? ''} />;
  return <div className={['hf-richtext', clamp && 'clamp'].filter(Boolean).join(' ')} dangerouslySetInnerHTML={{ __html: safe }} />;
}

export function RichTextEditor({ value, onChange, disabled, autoFocus, placeholder, id, 'aria-label': ariaLabel }: { value: string | null | undefined; onChange: (html: string) => void; disabled?: boolean; autoFocus?: boolean; placeholder?: string; id?: string; 'aria-label'?: string }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value ?? '',
    editable: !disabled,
    autofocus: autoFocus ? 'end' : false,
    editorProps: { attributes: { class: 'hf-richtext-body', id: id ?? '', 'aria-label': ariaLabel ?? 'Rich text', 'data-placeholder': placeholder ?? '' } },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
  });
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value ?? '') !== current && !editor.isFocused) editor.commands.setContent(value ?? '', { emitUpdate: false });
  }, [value, editor]);
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);
  if (!editor) return null;
  const T = ({ icon, label, active, run }: { icon: Parameters<typeof IconButton>[0]['icon']; label: string; active?: boolean; run: () => void }) => (
    <IconButton icon={icon} label={label} size="sm" aria-pressed={active} className={active ? 'active' : undefined} onMouseDown={(e) => e.preventDefault()} onClick={run} disabled={disabled} />
  );
  return (
    <div className="hf-richtext-editor">
      <div className="hf-rt-toolbar" role="toolbar" aria-label="Formatting">
        <T icon="bold" label="Bold" active={editor.isActive('bold')} run={() => editor.chain().focus().toggleBold().run()} />
        <T icon="italic" label="Italic" active={editor.isActive('italic')} run={() => editor.chain().focus().toggleItalic().run()} />
        <T icon="code" label="Code" active={editor.isActive('code')} run={() => editor.chain().focus().toggleCode().run()} />
        <span className="hf-rule vertical" style={{ height: 16, margin: '0 4px' }} />
        <T icon="type" label="Heading" active={editor.isActive('heading', { level: 3 })} run={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <T icon="list" label="Bullet list" active={editor.isActive('bulletList')} run={() => editor.chain().focus().toggleBulletList().run()} />
        <T icon="list-ordered" label="Numbered list" active={editor.isActive('orderedList')} run={() => editor.chain().focus().toggleOrderedList().run()} />
        <T icon="quote" label="Quote" active={editor.isActive('blockquote')} run={() => editor.chain().focus().toggleBlockquote().run()} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
